import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { RequestPasswordReset } from "@/domain/usecase/RequestPasswordReset";
import {
  initialRequestPasswordResetFormValues,
  requestPasswordResetSchema,
  type RequestPasswordResetFormValues,
} from "./passwordResetSchema.ts";
import {
  isAbortError,
  toFieldErrors,
  toUserMessage,
  type FieldErrors,
} from "./PasswordResetShared.ts";

type SubmitState =
  | { type: "idle" }
  | { type: "submitting" }
  | { type: "accepted" }
  | { type: "failed"; message: string };

type Args = {
  requestPasswordReset: RequestPasswordReset;
};

export function useRequestPasswordResetViewModel({
  requestPasswordReset,
}: Args) {
  const [values, setValues] = useState<RequestPasswordResetFormValues>(
    initialRequestPasswordResetFormValues,
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>({
    type: "idle",
  });

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  const isBusy = submitState.type === "submitting";

  const canSubmit = !isBusy && values.email.trim().length > 0;

  const setField = useCallback(
    <K extends keyof RequestPasswordResetFormValues>(
      field: K,
      value: RequestPasswordResetFormValues[K],
    ) => {
      setValues((current) => ({
        ...current,
        [field]: value,
      }));

      setFieldErrors((current) => {
        const next = { ...current };
        delete next[String(field)];
        return next;
      });

      if (submitState.type === "failed") {
        setSubmitState({ type: "idle" });
      }
    },
    [submitState.type],
  );

  const submit = useCallback(async () => {
    setSubmitState({ type: "idle" });
    setFieldErrors({});

    const parsed = requestPasswordResetSchema.safeParse(values);

    if (!parsed.success) {
      setFieldErrors(toFieldErrors(parsed.error));
      return;
    }

    submitAbortControllerRef.current?.abort();

    const controller = new AbortController();
    submitAbortControllerRef.current = controller;

    setSubmitState({ type: "submitting" });

    try {
      await requestPasswordReset.execute(
        {
          email: parsed.data.email,
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      setSubmitState({ type: "accepted" });
    } catch (error) {
      if (isAbortError(error)) return;

      setSubmitState({
        type: "failed",
        message: toUserMessage(
          error,
          "Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        ),
      });
    } finally {
      if (submitAbortControllerRef.current === controller) {
        submitAbortControllerRef.current = null;
      }
    }
  }, [requestPasswordReset, values]);

  return useMemo(
    () => ({
      values,
      fieldErrors,
      submitState,
      isBusy,
      canSubmit,
      actions: {
        setField,
        submit,
      },
    }),
    [values, fieldErrors, submitState, isBusy, canSubmit, setField, submit],
  );
}
